import React from "react"
import PropTypes from "prop-types"
import * as R from "ramda"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"

import { renderRichText } from "gatsby-source-contentful/rich-text"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Person from "../person"
import Teaser from "../teaser"
import Button from "../button"
import CollectionGrid from "../../components/collectionGrid"
import TeaserCard from "../../components/teasercard"

const Text = ({ children }) => <p className="whitespace-pre-wrap">{children}</p>

const renderComponent = component => {
  const __typename = R.path(["data", "target", "__typename"], component)

  switch (__typename) {
    case "ContentfulHenkilo":
      const personName = R.path(["data", "target", "name"], component)
      const personText = R.path(
        ["data", "target", "contentText", "contentText"],
        component
      )
      const personImage = R.path(
        ["data", "target", "image", "gatsbyImageData"],
        component
      )

      return (
        <div className="mb-10">
          <Person
            name={personName}
            image={personImage ? personImage : null}
            contentText={personText}
          />
        </div>
      )
    case "ContentfulPainike":
      const ctaText = R.path(
        ["data", "target", "ctaText"],
        component
      )
      const ctaTarget = R.path(
        ["data", "target", "ctaTarget"],
        component
      )
      const ctaColor = R.path(
        ["data", "target", "ctaColor"],
        component
      )
      return (
        <div className="mb-10">
          <Button url={ctaTarget} backgroundColor={ctaColor}>{ctaText}</Button>
        </div>
      )

    case "ContentfulNostoruudukko":
      const teasers = R.path(["data", "target", "teasers"], component)

      return (
        <div className="w-screen ml-[50%] translate-x-[-50%] max-w-6xl mx-auto mt-5 md:mt-10 px-5 mb-10">
          <CollectionGrid>
            {teasers.map(teaser => (
              <TeaserCard
                key={teaser.buttonLink}
                title={teaser.title}
                image={R.path(["gatsbyImageData"], teaser.image)}
                url={`${teaser.buttonLink}`}
                text={R.path(["contentText"], teaser.contentText)}
              />
            ))}
          </CollectionGrid>
        </div>
      )

    case "ContentfulNosto":
      const teaserTitle = R.path(["data", "target", "title"], component)
      const teaserText = R.path(
        ["data", "target", "contentText", "contentText"],
        component
      )
      const teaserImage = R.path(
        ["data", "target", "image", "gatsbyImageData"],
        component
      )
      const teaserButtonText = R.path(
        ["data", "target", "buttonText"],
        component
      )
      const teaserButtonLink = R.path(
        ["data", "target", "buttonLink"],
        component
      )
      const teaserBackgroundColor = R.path(
        ["data", "target", "backgroundColor"],
        component
      )
      const teaserReverseImage = R.path(
        ["data", "target", "reverseImage"],
        component
      )
      return (
        <div className="mb-12">
          <Teaser
            title={teaserTitle}
            image={getImage(teaserImage)}
            contentText={teaserText}
            buttonLink={teaserButtonLink}
            buttonText={teaserButtonText}
            backgroundColor={teaserBackgroundColor}
            reverseImage={teaserReverseImage}
          />
        </div>
      )

    default:
      return null
  }
}


const options = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: node => {
      if (node.data.target) {
        const { gatsbyImageData, description } = node.data.target
        return (
          <GatsbyImage
            image={getImage(gatsbyImageData)}
            alt={description}
            className="mb-10"
          />
        )
      } else {
        return null
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: node => {
      return renderComponent(node)
    },
    [BLOCKS.PARAGRAPH]: (node, children)  => {
      return (
        <Text>{children}</Text>
      )
    },
    [INLINES.ASSET_HYPERLINK]: (node, children)  => {
      if (node.data.target) {
        return (
          <a
            href={`https://${node.data.target.file.url}`}
            target="_blank"
          >
            {children}
          </a>
        )
      } else {
        return null
      }
    },
    [INLINES.HYPERLINK]: (node, children) => {
      return (
        <a
          href={node.data.uri}
          target="_blank"
        >
          <span>
            {children}
          </span>
          <span className="pl-2 pr-0.5">
            <span aria-hidden="true" className="inline-block w-3 h-3">
              <svg viewBox="0 0 22 22" width="22" height="22" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto fill-current">
                <path d="M8 6v2H2v12h12v-6h2v8H0V6h8zm14-6v11h-2V3.414L7.707 15.707l-1.414-1.414L18.585 2H11V0h11z"></path>
              </svg>
            </span>
          </span>
          <span className="sr-only">
            Avautuu uuteen ikkunaan
          </span>
        </a>
      )
    },
  },
}

const RichText = ({ richText }) => {
  return <div className="prose">{renderRichText(richText, options)}</div>
}

RichText.propTypes = {
  richText: PropTypes.any.isRequired,
}

export default RichText
